import React from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';

const RANGE_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const RANGE_COLORS = [
  '#4A90D9', // blue
  '#E67E22', // orange
  '#27AE60', // green
  '#8E44AD', // purple
  '#E74C3C', // red
  '#16A085', // teal
  '#F39C12', // amber
  '#2980B9', // dark blue
  '#D35400', // burnt orange
  '#1ABC9C', // turquoise
];

const getRangeColor = idx => RANGE_COLORS[idx % RANGE_COLORS.length];

const LinkFringeModal = ({
  show,
  onHide,
  selectedFringe,
  filteredFringe,
  isLoading,
  filteredRows,
  rangeColumns,
  rangeCheckedRows,
  onRangeCheckboxChange,
  onRangeSelectAll,
  onAddRange,
  onRemoveRange,
  onConfirm,
}) => {
  const getRangeLabel = idx =>
    idx < RANGE_LABELS.length ? RANGE_LABELS[idx] : `R${idx + 1}`;

  const isColumnAllChecked = rangeId =>
    filteredRows.length > 0 &&
    filteredRows.every(
      row => rangeCheckedRows[rangeId]?.[row.id.toString()] === true
    );

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="custom-modal"
      size={rangeColumns.length > 3 ? 'lg' : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              color: '#4d5abc',
              padding: '10px 0px',
              fontWeight: 'bold',
            }}
          >
            {selectedFringe?.type === 'group' ? 'Link Group' : 'Link Fringe'}
            <span
              style={{
                fontSize: '13px',
                fontWeight: 'normal',
                color: '#555',
                marginTop: '2px',
              }}
            >
              Please select the items you wish to link to {filteredFringe}
            </span>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '120px',
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : filteredRows.length > 0 ? (
          <div>
            {/* Range column headers */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0',
                marginBottom: '8px',
                paddingBottom: '8px',
                borderBottom: '1px solid #eee',
              }}
            >
              {/* Spacer matching the label column */}
              <div style={{ minWidth: '40px' }}>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '13px',
                    color: '#888',
                  }}
                >
                  Range:
                </span>
              </div>

              {rangeColumns.map((col, idx) => {
                const color = getRangeColor(idx);
                return (
                  <div
                    key={col.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '44px',
                      padding: '0 4px',
                      position: 'relative',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: '12px',
                        color: '#fff',
                        backgroundColor: color,
                        borderRadius: '4px',
                        padding: '1px 7px',
                        marginBottom: '4px',
                        lineHeight: '18px',
                      }}
                    >
                      {getRangeLabel(idx)}
                    </span>
                    <input
                      type="checkbox"
                      checked={isColumnAllChecked(col.id)}
                      onChange={e => onRangeSelectAll(col.id, e.target.checked)}
                      style={{
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                        accentColor: color,
                      }}
                      title={`Select all for Range ${getRangeLabel(idx)}`}
                    />
                    {rangeColumns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onRemoveRange(col.id)}
                        style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-2px',
                          background: 'none',
                          border: 'none',
                          color: '#999',
                          cursor: 'pointer',
                          fontSize: '11px',
                          padding: '0',
                          lineHeight: '1',
                        }}
                        title={`Remove Range ${getRangeLabel(idx)}`}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Add range button */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '36px',
                }}
              >
                <button
                  type="button"
                  onClick={onAddRange}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6c63ff',
                    cursor: 'pointer',
                    fontSize: '22px',
                    fontWeight: 'bold',
                    padding: '0 4px',
                    lineHeight: '1',
                  }}
                  title="Add new range"
                >
                  +
                </button>
              </div>

              {/* Item name column header */}
              <div style={{ flex: 1, paddingLeft: '8px' }} />
            </div>

            {/* Row list */}
            <div
              style={{
                maxHeight: '340px',
                overflowY: 'auto',
                paddingRight: '4px',
              }}
            >
              {filteredRows.map(row => {
                const rowIdStr = row.id.toString();
                return (
                  <div
                    key={row.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0',
                      padding: '5px 0',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                  >
                    {/* Spacer matching header */}
                    <div style={{ minWidth: '40px' }} />

                    {rangeColumns.map((col, idx) => {
                      const isChecked =
                        rangeCheckedRows[col.id]?.[rowIdStr] === true;
                      return (
                        <div
                          key={col.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '44px',
                            padding: '0 4px',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e =>
                              onRangeCheckboxChange(
                                col.id,
                                rowIdStr,
                                e.target.checked
                              )
                            }
                            style={{
                              cursor: 'pointer',
                              width: '16px',
                              height: '16px',
                              accentColor: getRangeColor(idx),
                            }}
                          />
                        </div>
                      );
                    })}

                    {/* Add-range spacer */}
                    <div style={{ minWidth: '36px' }} />

                    {/* Item name */}
                    <div
                      style={{
                        flex: 1,
                        paddingLeft: '8px',
                        fontSize: '14px',
                        color: '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.item || row.id} - {filteredFringe}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p>
            No rows found with this{' '}
            {selectedFringe?.type === 'group' ? 'group' : 'fringe'} applied.
          </p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            width: '100%',
          }}
        >
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Link/Unlink
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

LinkFringeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedFringe: PropTypes.object,
  filteredFringe: PropTypes.string,
  isLoading: PropTypes.bool,
  filteredRows: PropTypes.array,
  rangeColumns: PropTypes.array,
  rangeCheckedRows: PropTypes.object,
  onRangeCheckboxChange: PropTypes.func.isRequired,
  onRangeSelectAll: PropTypes.func.isRequired,
  onAddRange: PropTypes.func.isRequired,
  onRemoveRange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

LinkFringeModal.defaultProps = {
  selectedFringe: null,
  filteredFringe: '',
  isLoading: false,
  filteredRows: [],
  rangeColumns: [],
  rangeCheckedRows: {},
};

export default LinkFringeModal;
